import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useCurrentBusinessInfo from "../../hooks/useCurrentBusiness";

// ——— Netflix-ish tokens (design only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.78)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

type Props = {
  name?: string;
  logoUrl?: string;
  tagline?: string;
};

const ProfileImage: React.FC<Props> = () => {
  const logoUrl =
    "https://images.unsplash.com/photo-1512412046879-b85a1f368b3e?q=80&w=400&auto=format&fit=crop";
  const { currentBusiness } = useCurrentBusinessInfo();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "center", md: "flex-end" }}
      spacing={3}
      sx={{ px: { xs: 2, md: 0 } }}
    >
      <Box
        sx={{
          position: "relative",
          mt: { xs: 0, md: -10 },
          borderRadius: "24px",
          p: 1,
          background: COLORS.panel,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
        }}
      >
        <Box
          sx={{
            borderRadius: "20px",
            p: 1.25,
            bgcolor: COLORS.bg,
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          }}
        >
          <Avatar
            src={currentBusiness?.profile_image ?? logoUrl}
            alt={currentBusiness?.username ?? "Business Logo"}
            sx={{ width: 120, height: 120, borderRadius: "18px" }}
          />
        </Box>
      </Box>

      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={0.5}
        textAlign={{ xs: "center", md: "left" }}
        alignItems={"center"}
      >
        <Typography
          sx={{
            font: "var(--h5)",
            color: COLORS.text,
          }}
        >
          {currentBusiness?.business_name ?? "Business Name"}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          justifyContent={{ xs: "center", md: "flex-start" }}
        >
          {currentBusiness?.username && (
            <Chip
              label={`@${currentBusiness.username}`}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 500,
                color: COLORS.muted,
                borderColor: COLORS.border,
                background: COLORS.panel,
              }}
            />
          )}

          {currentBusiness?.business_type && (
            <Chip
              label={currentBusiness.business_type}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.875rem",
                px: 1.5,
                py: 0.5,
                borderRadius: 4,
                color: COLORS.text,
                borderColor: COLORS.border,
                background: COLORS.panel,
              }}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ProfileImage;
